import React, { type ComponentPropsWithoutRef } from 'react';
import classNames from 'classnames';

import { Locale } from '@fixter/i18n';
import type { CmsImage, CmsNumber, CmsRichText, CmsShortText } from '$cms/types';
import { PartOfRenault } from '$quantum/molecules/PartOfRenault';
import { GoogleReviewsSummary } from '$quantum/molecules/GoogleReviewsSummary';
import { TrustPilot } from '$quantum/atoms/TrustPilot';
import { useTrustpilotHeight } from '$quantum/atoms/TrustPilot/useTrustPilotHeight';
import { TRUSTPILOT_TEMPLATE_ID } from '$constants/trustpilot';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import { ContentComponentSection } from '@/components/atoms/ContentComponentSection';
import { Col, Row } from '@/components/atoms/Grid';
import { HeadingLevels } from '@/components/atoms/HeadingLevels';
import VrmLookupForm from '@/components/molecules/VrmLookupForm';

import styles from './index.module.scss';

interface Hero24Props extends ComponentPropsWithoutRef<'div'> {
  readonly heading: CmsRichText | CmsShortText;
  readonly postcodeLabel: CmsShortText;
  readonly vrmLabel: CmsShortText;
  readonly mainCta: CmsShortText;
  readonly locale: Locale;
  readonly invalidPostcodeMessage: CmsShortText;
  readonly invalidVRMMessage: CmsShortText;
  readonly missingPostcodeMessage: CmsShortText;
  readonly missingVRMMessage: CmsShortText;
  readonly googleReviewsStars?: CmsNumber;
  readonly googleReviewsTotalReviews?: CmsNumber;
  readonly googleReviewsLink?: CmsShortText;
  readonly googleReviewsReviewsText?: CmsShortText;
  readonly googleReviewsBasedOnText?: CmsShortText;
  readonly googleReviewsLogo?: CmsImage;
  readonly price?: CmsShortText;
  readonly headingRef?: React.RefObject<HTMLHeadingElement>;
  readonly vrmRef?: React.RefObject<HTMLHeadingElement>;
}

export function Hero24({
  heading,
  postcodeLabel,
  invalidPostcodeMessage,
  missingPostcodeMessage,
  vrmLabel,
  invalidVRMMessage,
  missingVRMMessage,
  mainCta,
  googleReviewsStars,
  googleReviewsTotalReviews,
  googleReviewsLink,
  googleReviewsReviewsText,
  googleReviewsBasedOnText,
  googleReviewsLogo,
  price,
  locale,
  className,
  headingRef,
  vrmRef,
  ...props
}: Hero24Props): JSX.Element {
  const trustpilotHeight = useTrustpilotHeight();

  const headingContent = (heading as CmsRichText).content
    ? renderRichTextContent((heading as CmsRichText).content)
    : (heading as CmsShortText).value;

  return (
    <ContentComponentSection
      className={classNames(Hero24.displayName, styles.component, className)}
      {...props}
    >
      <Row>
        <Col>
          <div className={styles.heading} ref={headingRef}>
            <HeadingLevels className={styles.headingText} semanticLevel={1} styleLevel={2}>
              {headingContent}
              {price?.value && <em className={styles.priceTag}>{price.value}</em>}
            </HeadingLevels>
          </div>
          <div ref={vrmRef} className={styles.vrm}>
            <VrmLookupForm
              locale={locale}
              translations={{
                textPostcode: postcodeLabel.value,
                textPostcodeInvalid: invalidPostcodeMessage.value,
                textPostcodeRequired: missingPostcodeMessage.value,
                textRegistrationNumber: vrmLabel.value,
                textRegistrationNumberInvalid: invalidVRMMessage.value,
                textRegistrationNumberRequired: missingVRMMessage.value,
                textCtaGetInstantQuote: mainCta.value,
              }}
            />
          </div>
          <div className={styles.lowerContainer}>
            {locale === Locale.FR_FR && googleReviewsStars?.number && (
              <GoogleReviewsSummary
                className={styles.googleReviews}
                rating={googleReviewsStars?.number}
                link={googleReviewsLink?.value}
                reviewsText={googleReviewsReviewsText?.value}
                providerLogo={
                  googleReviewsLogo || {
                    key: 'google-logo',
                    url: 'https://res.cloudinary.com/fxtr/image/upload/v1688549036/landing/logos/google_review_fv9hui.svg',
                    width: 36,
                    height: 31,
                    alt: 'Google Logo',
                  }
                }
                providerLogoWidth={googleReviewsLogo?.width || 36}
                providerLogoHeight={googleReviewsLogo?.height || 36}
              />
            )}
            {locale === Locale.EN_GB && (
              <TrustPilot
                className={styles.trustpilotReviews}
                templateId={TRUSTPILOT_TEMPLATE_ID}
                height={trustpilotHeight}
                width="100%"
                theme="dark"
              />
            )}
            <PartOfRenault className={styles.partOfRenault} layout="horizontal" preload />
          </div>
        </Col>
      </Row>
    </ContentComponentSection>
  );
}
Hero24.displayName = 'Hero24';
